import React, { useState } from 'react';
import styled from 'styled-components';
import { FaMapMarkerAlt, FaStar, FaTrash, FaPlus } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';

const FavoritesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--primary-color);
  color: black;
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
  
  svg {
    font-size: 1.25rem;
  }
`;

const FavoritesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const FavoriteCard = styled.div`
  background: var(--bg-card);
  border-radius: var(--border-radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FavoriteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FavoriteName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const FavoriteIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
`;

const FavoriteAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  
  svg {
    font-size: 1.25rem;
  }
`;

const FavoriteActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;
`;

const ActionButton = styled.button<{ variant?: 'delete' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: ${props => props.variant === 'delete' ? 'var(--error)' : 'var(--primary-color)'};
  color: ${props => props.variant === 'delete' ? 'white' : 'black'};
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
  
  svg {
    font-size: 1.25rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
  
  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--text-secondary);
  }
`;

const Favorites: React.FC = () => {
  const { t } = useLanguage();
  const [favorites, setFavorites] = useState([
    {
      id: 1,
      name: 'Maison',
      address: '123 rue de Paris, 75001 Paris',
      type: 'home'
    },
    {
      id: 2,
      name: 'Travail',
      address: '456 avenue des Champs-Élysées, 75008 Paris',
      type: 'work'
    },
    {
      id: 3,
      name: 'Aéroport CDG',
      address: '95700 Roissy-en-France',
      type: 'airport'
    }
  ]);
  
  const handleDelete = (id: number) => {
    setFavorites(favorites.filter(fav => fav.id !== id));
  };
  
  return (
    <FavoritesContainer>
      <Header>
        <Title>{t('app.favorites')}</Title>
        <AddButton>
          <FaPlus />
          <span>{t('app.addFavorite')}</span>
        </AddButton>
      </Header>
      
      {favorites.length > 0 ? (
        <FavoritesList>
          {favorites.map((favorite) => (
            <FavoriteCard key={favorite.id}>
              <FavoriteHeader>
                <FavoriteName>{favorite.name}</FavoriteName>
                <FavoriteIcon>
                  <FaStar />
                </FavoriteIcon>
              </FavoriteHeader>
              
              <FavoriteAddress>
                <FaMapMarkerAlt />
                <span>{favorite.address}</span>
              </FavoriteAddress>
              
              <FavoriteActions>
                <ActionButton>
                  <FaMapMarkerAlt />
                  <span>{t('app.useAddress')}</span>
                </ActionButton>
                <ActionButton variant="delete" onClick={() => handleDelete(favorite.id)}>
                  <FaTrash />
                  <span>{t('app.delete')}</span>
                </ActionButton>
              </FavoriteActions>
            </FavoriteCard>
          ))}
        </FavoritesList>
      ) : (
        <EmptyState>
          <FaStar />
          <p>{t('app.noFavorites')}</p>
        </EmptyState>
      )}
    </FavoritesContainer>
  );
};

export default Favorites; 